import { io, type Socket } from "socket.io-client";

import {
  adaptCallEndedEvent,
  adaptCallIncomingEvent,
  adaptCallSnapshotEvent,
  adaptCallTakenEvent,
  adaptObserverJoinedEvent,
  adaptObserverLeftEvent,
  adaptOperatorLeftEvent,
  adaptQueueClaimedEvent,
  adaptQueueClaimReleasedEvent,
} from "~/services/calling/adapters";
import { SocketEvents } from "~/services/calling/socketEvents";
import type {
  AgentPresencePayload,
  CallEndedPayload,
  CallIncomingPayload,
  CallSnapshotPayload,
  CallTakenPayload,
  QueueClaimedPayload,
  QueueClaimReleasedPayload,
  ConferenceInvitePayload,
  ConferenceInviteRequest,
  ObserverJoinedPayload,
  ObserverLeftPayload,
  SocketClientEvents,
  SocketServerEvents,
} from "~/services/calling/types";

export type SocketEventMeta = {
  eventName: string;
  source: "named" | "alias" | "wildcard";
};

type AppSocketHandlers = {
  onConnect?: () => void;
  onDisconnect?: (reason: string) => void;
  onConnectError?: (error: Error) => void;
  onReconnectAttempt?: (attempt: number) => void;
  onReconnect?: () => void;
  onIncomingCall?: (payload: CallIncomingPayload, meta?: SocketEventMeta) => void;
  onCallTaken?: (payload: CallTakenPayload, meta?: SocketEventMeta) => void;
  onCallSnapshot?: (payload: CallSnapshotPayload, meta?: SocketEventMeta) => void;
  onCallEnded?: (payload: CallEndedPayload, meta?: SocketEventMeta) => void;
  onAgentList?: (payload: AgentPresencePayload[]) => void;
  onAgentStatus?: (payload: AgentPresencePayload) => void;
  onInviteConference?: (payload: ConferenceInvitePayload) => void;
  onObserverJoined?: (payload: ObserverJoinedPayload) => void;
  onObserverLeft?: (payload: ObserverLeftPayload) => void;
  onQueueClaimed?: (payload: QueueClaimedPayload) => void;
  onQueueClaimReleased?: (payload: QueueClaimReleasedPayload) => void;
  onQueueResyncRequested?: () => void;
  onAnyEvent?: (eventName: string, args: unknown[]) => void;
};

type SocketIdentity = {
  userId?: string;
  agentId?: string;
  email?: string;
  extension?: string;
  sipUsername?: string;
};

const SOCKET_REGISTER_EVENT_ENV_KEYS = [
  "VITE_SOCKET_REGISTER_EVENT",
] as const;

function asString(value: unknown): string | undefined {
  if (typeof value === "string") {
    const t = value.trim();
    return t.length > 0 ? t : undefined;
  }
  if (typeof value === "number" && Number.isFinite(value)) return String(value);
  return undefined;
}

function getConfiguredRegisterEvents(): string[] {
  const env = import.meta.env as Record<string, string | undefined>;
  for (const key of SOCKET_REGISTER_EVENT_ENV_KEYS) {
    const value = asString(env[key]);
    if (!value) continue;
    const parsed = value.split(",").map((s) => s.trim()).filter(Boolean);
    if (parsed.length === 0) continue;
    const disable = parsed.some((s) =>
      ["none", "off", "false", "0"].includes(s.toLowerCase())
    );
    if (disable) return [];
    return [...new Set(parsed)];
  }
  return ["register"];
}

const configuredRegisterEvents = getConfiguredRegisterEvents();

function normalizeSocketIdentity(identity?: SocketIdentity | null): SocketIdentity {
  const out: SocketIdentity = {};
  const userId = asString(identity?.userId);
  if (userId) out.userId = userId;
  const agentId = asString(identity?.agentId);
  if (agentId) out.agentId = agentId;
  const email = asString(identity?.email);
  if (email) out.email = email;
  const extension = asString(identity?.extension);
  if (extension) out.extension = extension;
  const sipUsername = asString(identity?.sipUsername);
  if (sipUsername) out.sipUsername = sipUsername;
  return out;
}

function buildSocketAuthPayload(token: string | null, identity: SocketIdentity) {
  const payload: Record<string, string> = {};
  const t = asString(token);
  if (t) {
    payload.token = t;
    payload.authorization = `Bearer ${t}`;
  }
  if (identity.userId) payload.userId = identity.userId;
  if (identity.agentId) payload.agentId = identity.agentId;
  if (identity.email) payload.email = identity.email;
  if (identity.extension) payload.extension = identity.extension;
  if (identity.sipUsername) payload.sipUsername = identity.sipUsername;
  return payload;
}

function buildSocketQuery(identity: SocketIdentity) {
  const query: Record<string, string> = {};
  if (identity.userId) query.userId = identity.userId;
  if (identity.agentId) query.agentId = identity.agentId;
  if (identity.extension) query.extension = identity.extension;
  if (identity.sipUsername) query.sipUsername = identity.sipUsername;
  return query;
}

function buildSocketRegisterPayload(identity: SocketIdentity) {
  const payload: Record<string, string> = {};
  if (identity.userId) payload.userId = identity.userId;
  if (identity.agentId) payload.agentId = identity.agentId;
  if (identity.email) payload.email = identity.email;
  if (identity.extension) payload.extension = identity.extension;
  if (identity.sipUsername) payload.sipUsername = identity.sipUsername;
  return payload;
}

function buildSocketFingerprint(token: string | null, identity: SocketIdentity) {
  return JSON.stringify({ token: asString(token) ?? null, identity });
}

export class AppSocket {
  private socket: Socket<SocketServerEvents, SocketClientEvents> | null = null;
  private currentUrl: string | null = null;
  private currentToken: string | null = null;
  private currentIdentity: SocketIdentity = {};
  private currentFingerprint: string | null = null;
  private isConnecting = false;
  private connectGeneration = 0;
  private connectTimer: number | null = null;

  private clearConnectTimer() {
    if (this.connectTimer == null) return;
    window.clearTimeout(this.connectTimer);
    this.connectTimer = null;
  }

  private destroyActiveSocket() {
    const socket = this.socket;
    this.clearConnectTimer();
    if (!socket) return;

    this.connectGeneration += 1;
    this.isConnecting = false;
    this.socket = null;

    socket.removeAllListeners();
    socket.io.off();

    try {
      socket.io.reconnection(false);
      if (socket.connected) {
        socket.disconnect();
        return;
      }

      // Abort an in-flight handshake via the manager instead of racing
      // socket.disconnect(), which surfaces browser console noise. The
      // manager's close() is not in the public socket.io-client types.
      (socket.io as unknown as { close: () => void }).close();
    } catch {
      // Best-effort — orphaned engine will time out on its own.
    }
  }

  private emitConfiguredRegisterEvents() {
    if (!this.socket || configuredRegisterEvents.length === 0) return;
    const payload = buildSocketRegisterPayload(this.currentIdentity);
    if (Object.keys(payload).length === 0) return;
    for (const eventName of configuredRegisterEvents) {
      this.socket.emit(eventName as keyof SocketClientEvents, payload as any);
    }
  }

  private emitIdentify() {
    if (!this.socket) {
      return;
    }

    const extension = this.currentIdentity.extension;
    if (!extension) {
      return;
    }

    this.socket.emit(SocketEvents.identify, { extension: String(extension) });
  }

  private setupPresenceHandlers(handlers: AppSocketHandlers = {}) {
    if (!this.socket) return;

    const socket = this.socket;

    socket.on(SocketEvents.ping, () => {
      socket.emit(SocketEvents.pong);
    });

    socket.io.on("reconnect_attempt", (attempt) => {
      handlers.onReconnectAttempt?.(attempt);
    });

    socket.io.on("reconnect", () => {
      handlers.onReconnect?.();
      this.emitIdentify();
    });
  }

  connect(
    url: string,
    token: string | null,
    handlers: AppSocketHandlers = {},
    identity?: SocketIdentity
  ) {
    const normalizedIdentity = normalizeSocketIdentity(identity);
    if (!normalizedIdentity.extension) {
      return;
    }

    const fingerprint = buildSocketFingerprint(token, normalizedIdentity);
    const shouldReuse =
      this.socket &&
      this.currentUrl === url &&
      this.currentFingerprint === fingerprint &&
      (this.socket.connected || this.isConnecting || this.connectTimer != null);
    if (shouldReuse) {
      return;
    }

    this.destroyActiveSocket();

    this.currentUrl = url;
    this.currentToken = token;
    this.currentIdentity = normalizedIdentity;
    this.currentFingerprint = fingerprint;
    this.isConnecting = true;
    const generation = this.connectGeneration;
    this.socket = io(url, {
      transports: ["websocket", "polling"],
      path: "/socket.io",
      timeout: 10000,
      autoConnect: false,
      auth: buildSocketAuthPayload(token, normalizedIdentity),
      query: buildSocketQuery(normalizedIdentity),
    });

    const socket = this.socket;

    this.setupPresenceHandlers(handlers);

    socket.on("connect", () => {
      if (generation !== this.connectGeneration) return;
      this.isConnecting = false;
      this.emitIdentify();
      this.emitConfiguredRegisterEvents();
      handlers.onConnect?.();
    });
    socket.on("disconnect", (reason) => {
      if (generation !== this.connectGeneration) return;
      this.isConnecting = false;
      handlers.onDisconnect?.(reason);
    });
    socket.on("connect_error", (error) => {
      if (generation !== this.connectGeneration) return;
      this.isConnecting = false;
      handlers.onConnectError?.(error);
    });

    socket.on(
      SocketEvents.callIncoming,
      (payload: unknown) => {
        const normalized = adaptCallIncomingEvent(payload);
        if (normalized) {
          handlers.onIncomingCall?.(normalized, {
            eventName: SocketEvents.callIncoming,
            source: "named",
          });
          handlers.onQueueResyncRequested?.();
        }
      }
    );
    socket.on(
      SocketEvents.callIncomingEnriched,
      (payload: unknown) => {
        const normalized = adaptCallIncomingEvent(payload);
        if (normalized) {
          handlers.onIncomingCall?.(normalized, {
            eventName: SocketEvents.callIncomingEnriched,
            source: "alias",
          });
          handlers.onQueueResyncRequested?.();
        }
      }
    );
    socket.on(SocketEvents.callEnded, (payload: unknown) => {
      const normalized = adaptCallEndedEvent(payload);
      if (normalized) {
        handlers.onCallEnded?.(normalized, {
          eventName: SocketEvents.callEnded,
          source: "named",
        });
      }
      handlers.onQueueResyncRequested?.();
    });
    socket.on(SocketEvents.callEvent, () => {
      handlers.onQueueResyncRequested?.();
    });
    socket.on(SocketEvents.callAnswered, () => {
      handlers.onQueueResyncRequested?.();
    });
    socket.on(SocketEvents.callTaken, (payload: unknown) => {
      const normalized = adaptCallTakenEvent(payload);
      if (normalized) {
        handlers.onCallTaken?.(normalized, {
          eventName: SocketEvents.callTaken,
          source: "named",
        });
      }
    });
    socket.on(SocketEvents.callSnapshot, (payload: unknown) => {
      const normalized = adaptCallSnapshotEvent(payload);
      if (normalized) {
        handlers.onCallSnapshot?.(normalized, {
          eventName: SocketEvents.callSnapshot,
          source: "named",
        });
      }
    });

    socket.on(SocketEvents.agentList, (payload) => handlers.onAgentList?.(payload));
    socket.on(SocketEvents.agentStatus, (payload) => handlers.onAgentStatus?.(payload));
    socket.on(SocketEvents.inviteConference, (payload) =>
      handlers.onInviteConference?.(payload)
    );

    socket.on(SocketEvents.callObserverJoined, (payload: unknown) => {
      const normalized = adaptObserverJoinedEvent(payload);
      if (normalized) handlers.onObserverJoined?.(normalized);
    });
    socket.on(SocketEvents.callObserverLeft, (payload: unknown) => {
      const normalized = adaptObserverLeftEvent(payload);
      if (normalized) handlers.onObserverLeft?.(normalized);
    });
    socket.on(SocketEvents.callOperatorLeft, (payload: unknown) => {
      void adaptOperatorLeftEvent(payload);
    });
    socket.on(SocketEvents.queueClaimed, (payload: unknown) => {
      const normalized = adaptQueueClaimedEvent(payload);
      if (normalized) handlers.onQueueClaimed?.(normalized);
    });
    socket.on(SocketEvents.queueClaimReleased, (payload: unknown) => {
      const normalized = adaptQueueClaimReleasedEvent(payload);
      if (normalized) handlers.onQueueClaimReleased?.(normalized);
    });

    socket.onAny((eventName, ...args) => {
      handlers.onAnyEvent?.(String(eventName), args);
    });

    this.clearConnectTimer();
    this.connectTimer = window.setTimeout(() => {
      this.connectTimer = null;
      if (generation !== this.connectGeneration) return;
      if (!this.socket) return;
      this.socket.connect();
    }, 0);
  }

  updateAuthToken(token: string | null) {
    if (token === this.currentToken) return;
    this.currentToken = token;
    this.currentFingerprint = buildSocketFingerprint(token, this.currentIdentity);
    if (!this.socket) return;

    this.socket.auth = buildSocketAuthPayload(token, this.currentIdentity);
    if (this.socket.connected) {
      this.isConnecting = true;
      this.socket.disconnect();
      this.socket.connect();
      return;
    }

    this.destroyActiveSocket();
    this.currentUrl = null;
    this.currentFingerprint = null;
  }

  disconnect() {
    this.clearConnectTimer();
    this.destroyActiveSocket();
    this.currentUrl = null;
    this.currentToken = null;
    this.currentIdentity = {};
    this.currentFingerprint = null;
    this.isConnecting = false;
  }

  inviteConference(payload: ConferenceInviteRequest) {
    if (!this.socket) return;
    this.socket.emit(SocketEvents.inviteConference, payload);
  }

  isConnected() {
    return Boolean(this.socket?.connected);
  }

  isReconnecting() {
    const manager = this.socket?.io as { reconnecting?: boolean } | undefined;
    return Boolean(manager?.reconnecting);
  }

  getSocketId() {
    return this.socket?.id ?? null;
  }
}
